import api from '@/client/axiosInstance';
import { Promotion } from '@/types';
import axios from 'axios';

interface PromotionFilters {
  status?: string;
  effect?: string;
  affected?: string;
  startDate?: Date;
  endDate?: Date;
  page?: number;
  limit?: number;
}

const handleError = (error: unknown, message: string): never => {
  if (axios.isAxiosError(error)) {
    throw new Error(
      `${message}: ${error.response?.data?.message || error.message}`,
    );
  } else {
    throw new Error(`${message}: Unknown error occurred`);
  }
};

export const getAllPromotions = async () => {
  try {
    const response = await api.get('users/promotions');
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching promotions');
    return [];
  }
};

export const getAdminPromotions = async (filters: PromotionFilters) => {
  try {
    // construct query string
    const queryString = new URLSearchParams();
    if (filters.status && filters.status !== 'All') {
      queryString.append(
        'status',
        filters.status === 'Running' ? 'active' : 'inactive',
      );
    }
    if (filters.effect && filters.effect !== 'All') {
      queryString.append('effect', filters.effect.toLowerCase());
    }
    if (filters.affected && filters.affected !== 'All') {
      queryString.append('affected', filters.affected.toLowerCase());
    }
    if (filters.startDate) {
      queryString.append('startingDate', filters.startDate.toISOString());
    }
    if (filters.endDate) {
      queryString.append('endingDate', filters.endDate.toISOString());
    }
    if (filters.page) {
      queryString.append('page', filters.page.toString());
    }
    if (filters.limit) {
      queryString.append('limit', filters.limit.toString());
    }

    const response = await api.get(`admin/promotions?${queryString}`);

    const formattedPromotions = response?.data?.promotions.map(
      (promotion: Promotion) => ({
        ...promotion,
        key: promotion.id,
        name: promotion.name,
        description: promotion.description,
        effect: promotion.effect,
        affected: promotion.affected,
        status: promotion.status,
        startDate: new Date(promotion.startDate),
        endDate: new Date(promotion.endDate),
      }),
    );
    return {
      total: response?.data?.total || 0,
      page: response?.data?.page || 0,
      limit: response?.data?.limit || 0,
      promotions: formattedPromotions || [],
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    return {
      total: 0,
      page: 0,
      limit: 0,
      promotions: [],
    };
  }
};

export const updatePromotionById = async (
  id: string,
  data: Partial<Promotion>,
) => {
  try {
    const response = await api.patch(`admin/promotions/${id}`, data);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const deletePromotionById = async (id: string) => {
  try {
    const response = await api.delete(`admin/promotions/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const createPromotion = async (data: Promotion) => {
  try {
    const response = await api.post('admin/promotions', data);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};
